import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Divider,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import MaterialGrid from '@material-ui/core/Grid';
import PrimaryButton from 'common/components/buttons/primaryButton';
import SecondaryButton from 'common/components/buttons/secondaryButton';
import {
  InputLabelOutlined,
  SelectLabel,
} from 'common/components/fields/fields';
import LoadingProgress from 'common/components/progress/loading';
import { DDDMask, phoneWithoutDDDMask } from 'helpers/masks';
import { FORM_RULES } from 'helpers/validations';
import { getAdditionalData } from 'modules/owner/pages/signupCompany/signupCompanyActions.js';
import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, Form, getFormValues, initialize, reduxForm } from 'redux-form';
import {
  savePaymentLinkGatewayInfo,
  putPaymentLinkGatewayInfo,
  searchRecipientByDocument,
  getPaymentLinkAccounts,
  getPaymentLinkAccountDetail,
  deletePaymentLinkAccount,
} from '../profileActions';
import { isMobile } from 'react-device-detect';
import debounce from 'lodash/debounce';
import {
  DadosEmpresaFields,
  DadosPessoaFisicaFields,
  RepresentanteLegalFields,
  SituacaoRecebedor,
} from './camposRecebedor';
import {
  campoTravado,
  criarGuardaDaBusca,
  formularioParaPayload,
  normalizarAgencia,
  normalizarConta,
  normalizarDigito,
  propsDeCampoTravavel,
  recebedorParaFormulario,
  ROTULOS_SITUACAO,
  TIPOS_CONTA,
} from './recebedorFormulario';

const FORMULARIO = 'PaymentLinkGatewayForm';
const OPCOES_DO_FORMULARIO = {
  prefixo: 'payment_link_account',
  campoSocios: 'paymentLinkManagingPartners',
};

const OBRIGATORIO = [FORM_RULES.required];
const VALIDA_EMAIL = [FORM_RULES.required, FORM_RULES.email];
const VALIDA_URL = [FORM_RULES.required, FORM_RULES.url];

// Mapeamento de código do banco para nome
const BANK_NAMES = {
  '001': 'Banco do Brasil',
  '033': 'Santander',
  104: 'Caixa Econômica',
  237: 'Bradesco',
  341: 'Itaú',
  756: 'Sicoob',
  748: 'Sicredi',
  '077': 'Inter',
  260: 'Nubank',
  336: 'C6 Bank',
};

let PaymentLinkGatewayForm = function (props) {
  const { formValues, dispatch } = props;
  const establishment = props.auth.user.establishments?.[0];
  const [searchingRecipient, setSearchingRecipient] = useState(false);
  const [existingRecipient, setExistingRecipient] = useState(null);
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  // Dados vindos da Pagar.me (edição ou busca por CPF): o que já foi enviado
  // fica travado, porque a v5 não deixa editar campo preenchido.
  const [valoresIniciais, setValoresIniciais] = useState(null);
  const [recebedor, setRecebedor] = useState(null);
  // Busca por CPF e edição disputam o mesmo formulário; a guarda descarta a
  // resposta que chega depois de uma ação mais nova. Criada uma vez só.
  const [guarda] = useState(criarGuardaDaBusca);

  const travado = (caminho) =>
    campoTravado(valoresIniciais, caminho, recebedor);
  const tipoDeConta = formValues?.payment_link_account?.bankAccountType;

  // Carregar contas existentes
  const loadAccounts = async () => {
    if (establishment?.id) {
      setLoadingAccounts(true);
      const accounts = await props.getPaymentLinkAccounts(establishment.id);
      setExistingAccounts(accounts || []);
      setLoadingAccounts(false);
    }
  };

  useEffect(() => {
    props.getAdditionalData();
    loadAccounts();
  }, []);

  // Busca recebedor já existente pelo CPF digitado. Documento que não é do
  // usuário volta `restricted` e segue como cadastro novo.
  const searchRecipientByCpf = useCallback(
    debounce(async (cpf) => {
      if (!cpf || cpf.replace(/\D/g, '').length < 11) return;
      const ficha = guarda.iniciarBusca();
      if (ficha === null) return;

      setSearchingRecipient(true);
      try {
        const result = await searchRecipientByDocument(cpf);
        // Conta aberta para edição, formulário fechado ou outro CPF buscado
        // enquanto esta resposta não chegava: aplicá-la apagaria dados mais
        // novos e destravaria campos da conta editada.
        if (!guarda.buscaVigente(ficha)) return;
        if (result.found && result.recipient) {
          const { formulario, recebedor: encontrado } = recebedorParaFormulario(
            result.recipient,
            OPCOES_DO_FORMULARIO
          );
          dispatch(initialize(FORMULARIO, formulario, true));
          setExistingRecipient({ id: result.recipient.id });
          setValoresIniciais(formulario);
          setRecebedor(encontrado);
        } else {
          setExistingRecipient(null);
          setValoresIniciais(null);
          setRecebedor(null);
        }
      } catch (e) {
        console.error('Erro ao buscar recebedor');
      } finally {
        // Busca vencida não mexe no indicador: quem a venceu cuida dele.
        if (guarda.buscaVigente(ficha)) setSearchingRecipient(false);
      }
    }, 800),
    [dispatch]
  );

  // Observar mudanças no campo CPF e buscar recebedor
  useEffect(() => {
    const cpf = formValues?.payment_link_account?.bankAccount?.cpf;
    if (cpf && !editingAccount && !guarda.emEdicao()) {
      searchRecipientByCpf(cpf);
    }
  }, [formValues?.payment_link_account?.bankAccount?.cpf, editingAccount]);

  const limparFormulario = () => {
    // Antes de qualquer estado: busca pendente ou em voo não pode reabrir o
    // formulário com os dados da conta que acabou de ser fechada.
    guarda.encerrar();
    searchRecipientByCpf.cancel();
    // Formulário limpo antes de sair do modo edição: na ordem inversa, o efeito
    // do CPF veria o CPF da conta editada sem edição e agendaria uma busca.
    dispatch(initialize(FORMULARIO, {}));
    setSearchingRecipient(false);
    setShowForm(false);
    setEditingAccount(null);
    setExistingRecipient(null);
    setValoresIniciais(null);
    setRecebedor(null);
  };

  function onSubmit(values) {
    const payload = formularioParaPayload(
      values,
      establishment,
      OPCOES_DO_FORMULARIO
    );

    const requisicao = editingAccount
      ? props.putPaymentLinkGatewayInfo(payload, editingAccount.id)
      : props.savePaymentLinkGatewayInfo({
          ...payload,
          existingRecipientId: existingRecipient?.id,
        });

    requisicao.then((sucesso) => {
      if (sucesso === false) return;
      loadAccounts();
      limparFormulario();
    });
  }

  // Deletar conta
  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('Tem certeza que deseja excluir esta conta?')) {
      const success = await props.deletePaymentLinkAccount(accountId);
      if (success) {
        loadAccounts();
      }
    }
  };

  // Editar conta - preencher formulário com os dados relidos da Pagar.me
  const handleEditAccount = async (account) => {
    const ficha = guarda.pedirEdicao();
    const detalhe = await props.getPaymentLinkAccountDetail(account.id);
    // Falha no detalhe mantém o formulário como estava. Outro clique em editar
    // ou Cancelar durante o carregamento vence este pedido.
    if (!detalhe || !guarda.confirmarEdicao(ficha)) return;
    // Daqui em diante nenhuma busca por CPF é aplicada; a pendente nem sai.
    searchRecipientByCpf.cancel();
    setSearchingRecipient(false);
    // Modo edição marcado ANTES do initialize: o CPF que ele preenche não
    // pode disparar a busca por documento no meio da edição.
    setEditingAccount(account);
    setExistingRecipient(null);
    setValoresIniciais(detalhe.formulario);
    setRecebedor(detalhe.recebedor);
    setShowForm(true);
    // Sem manter valores sujos: sobras de outro cadastro não podem se misturar
    // com os dados da conta editada.
    dispatch(initialize(FORMULARIO, detalhe.formulario, false));
  };

  if (
    props.signupCompany.additionalDataLoading ||
    props.profile.pagarmeLoading
  ) {
    return <LoadingProgress />;
  }

  const banks = props.signupCompany.additionalData?.banks || [];
  const sortedBanks = banks.sort(function (a, b) {
    if (a.number < b.number) {
      return -1;
    }
    if (a.number > b.number) {
      return 1;
    }
    return 0;
  });
  const sufixoPj = isMobile ? '' : tipoDeConta === 'pj' ? ' (PJ)' : '';

  return (
    <>
      {/* Lista de contas existentes */}
      <MaterialGrid item container xs={12} style={{ padding: '12px 0' }}>
        <Typography variant="body1" style={{ fontWeight: 'bold' }}>
          Contas cadastradas para Link de Pagamento
        </Typography>
      </MaterialGrid>

      {loadingAccounts ? (
        <Box display="flex" alignItems="center" style={{ padding: 16 }}>
          <CircularProgress size={20} style={{ marginRight: 12 }} />
          <Typography variant="body2">Carregando contas...</Typography>
        </Box>
      ) : existingAccounts.length > 0 ? (
        <MaterialGrid container spacing={2} style={{ marginBottom: 16 }}>
          {existingAccounts.map((account, index) => (
            <MaterialGrid item xs={12} key={account.id}>
              <Paper style={{ padding: 16, backgroundColor: '#f5f5f5' }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: 'bold' }}
                    >
                      {account.holder_name ||
                        account.legal_representative_name ||
                        account.name ||
                        'Conta ' + (index + 1)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {account.document ? `CPF/CNPJ: ${account.document}` : ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Banco:{' '}
                      {BANK_NAMES[account.bank_code] ||
                        account.bank_code ||
                        '-'}{' '}
                      | Ag: {account.agency || '-'} | Conta:{' '}
                      {account.account_number || '-'}-{account.account_dv || ''}
                    </Typography>
                    {account.status ? (
                      <Typography variant="body2" color="textSecondary">
                        Situação na Pagar.me:{' '}
                        {ROTULOS_SITUACAO[account.status] || account.status}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box>
                    <IconButton
                      onClick={() => handleEditAccount(account)}
                      color="primary"
                      title="Editar"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteAccount(account.id)}
                      title="Excluir"
                      style={{ color: '#f44336' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            </MaterialGrid>
          ))}
        </MaterialGrid>
      ) : (
        <Box
          style={{
            padding: 16,
            backgroundColor: '#fff3e0',
            borderRadius: 4,
            marginBottom: 16,
          }}
        >
          <Typography variant="body2">
            Nenhuma conta cadastrada para Link de Pagamento.
          </Typography>
        </Box>
      )}

      {/* Botão para adicionar nova conta */}
      {!showForm && (
        <Box style={{ marginBottom: 16 }}>
          <PrimaryButton onClick={() => setShowForm(true)}>
            Adicionar Nova Conta
          </PrimaryButton>
        </Box>
      )}

      {/* Formulário para adicionar ou editar conta */}
      {showForm && (
        <Form role="form" onSubmit={props.handleSubmit(onSubmit)} noValidate>
          <Divider style={{ margin: '16px 0' }} />
          <MaterialGrid item container xs={12} style={{ padding: '12px 0' }}>
            <Typography variant="body1">
              {editingAccount
                ? 'Dados da conta para recebimento via Link de Pagamento'
                : 'Dados da nova conta para recebimento via Link de Pagamento'}
            </Typography>
          </MaterialGrid>

          {editingAccount ? <SituacaoRecebedor recebedor={recebedor} /> : null}

          {searchingRecipient && (
            <MaterialGrid container spacing={3} style={{ marginBottom: 16 }}>
              <MaterialGrid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{
                    padding: 12,
                    backgroundColor: '#e3f2fd',
                    borderRadius: 4,
                  }}
                >
                  <CircularProgress size={20} style={{ marginRight: 12 }} />
                  <Typography variant="body2">
                    Buscando dados cadastrados no Pagar.me...
                  </Typography>
                </Box>
              </MaterialGrid>
            </MaterialGrid>
          )}

          {existingRecipient && !searchingRecipient && (
            <MaterialGrid container spacing={3} style={{ marginBottom: 16 }}>
              <MaterialGrid item xs={12}>
                <Box
                  style={{
                    padding: 12,
                    backgroundColor: '#e8f5e9',
                    borderRadius: 4,
                    border: '1px solid #4caf50',
                  }}
                >
                  <Typography variant="body2" style={{ color: '#2e7d32' }}>
                    Os dados foram preenchidos a partir do cadastro existente na
                    Pagar.me e serão apenas vinculados a esta conta.
                  </Typography>
                </Box>
              </MaterialGrid>
            </MaterialGrid>
          )}
          <MaterialGrid container spacing={3}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="E-mail"
                name="payment_link_account.email"
                required
                {...propsDeCampoTravavel(
                  travado('payment_link_account.email'),
                  VALIDA_EMAIL
                )}
                margin="normal"
              />
            </MaterialGrid>
            <MaterialGrid item xs={4} md={2}>
              <Field
                component={InputLabelOutlined}
                label="DDD"
                name="payment_link_account.phone.ddd"
                required
                {...propsDeCampoTravavel(
                  travado('payment_link_account.phone.ddd'),
                  OBRIGATORIO
                )}
                margin="normal"
                {...DDDMask}
              />
            </MaterialGrid>
            <MaterialGrid item xs={8} md={4}>
              <Field
                component={InputLabelOutlined}
                label="Número"
                name="payment_link_account.phone.number"
                required
                {...propsDeCampoTravavel(
                  travado('payment_link_account.phone.number'),
                  OBRIGATORIO
                )}
                margin="normal"
                {...phoneWithoutDDDMask}
              />
            </MaterialGrid>
            {isMobile ? (
              <MaterialGrid item container xs={12} style={{ paddingBottom: 0 }}>
                <Typography variant="body2">
                  Sua empresa na rede (site, linkedIn, Instagram, Facebook):
                </Typography>
              </MaterialGrid>
            ) : null}
            <MaterialGrid item xs={12}>
              <Field
                component={InputLabelOutlined}
                label={`URL${
                  isMobile ? '' : ' (site, linkedin, instagram, facebook)'
                }`}
                name="payment_link_account.businessUrl"
                required
                {...propsDeCampoTravavel(
                  travado('payment_link_account.businessUrl'),
                  VALIDA_URL
                )}
                margin="normal"
              />
            </MaterialGrid>
          </MaterialGrid>
          <MaterialGrid item container xs={12} style={{ paddingTop: '12px' }}>
            <Typography variant="body2">Selecione o tipo de conta</Typography>
          </MaterialGrid>
          <MaterialGrid container spacing={3}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={SelectLabel}
                label="Tipo de conta"
                name="payment_link_account.bankAccountType"
                validate={OBRIGATORIO}
                options={[
                  {
                    value: 'pf',
                    label: 'Pessoa Física',
                  },
                  {
                    value: 'pj',
                    label: 'Pessoa Jurídica',
                  },
                ]}
                required
                margin="normal"
                disabled={Boolean(editingAccount)}
              />
            </MaterialGrid>
          </MaterialGrid>
          {tipoDeConta === 'pj' ? (
            <>
              <DadosEmpresaFields
                prefixo="payment_link_account"
                travado={travado}
                establishment={establishment}
              />
              <RepresentanteLegalFields
                prefixo="paymentLinkManagingPartners"
                travado={travado}
              />
            </>
          ) : tipoDeConta === 'pf' ? (
            <DadosPessoaFisicaFields
              prefixo="payment_link_account"
              travado={travado}
              cpfTravado={Boolean(editingAccount)}
            />
          ) : null}
          {tipoDeConta ? (
            <>
              <MaterialGrid
                item
                container
                xs={12}
                style={{ padding: '12px 0' }}
              >
                <Typography variant="body1">
                  Dados bancários para recebimento via Link de Pagamento
                </Typography>
                {isMobile && tipoDeConta === 'pj' ? (
                  <Typography variant="caption">
                    Atenção: Preencha os campos com as informações relacionadas
                    à sua conta PJ
                  </Typography>
                ) : null}
                {tipoDeConta === 'pj' ? (
                  <Typography variant="body2" style={{ width: '100%' }}>
                    Titular: {establishment?.social_name || '-'}
                  </Typography>
                ) : null}
              </MaterialGrid>
              <MaterialGrid container spacing={3}>
                <MaterialGrid item xs={12} md={6}>
                  <Field
                    component={SelectLabel}
                    label={`Banco${sufixoPj}`}
                    name="payment_link_account.bankAccount.bankNumber"
                    validate={OBRIGATORIO}
                    options={sortedBanks.map((item) => ({
                      label: `${item.number} ${item.name}`,
                      value: item.number,
                    }))}
                    required
                    margin="normal"
                  />
                </MaterialGrid>
                <MaterialGrid
                  item
                  container
                  xs={12}
                  style={{ paddingBottom: 0 }}
                >
                  <Typography variant="body2">Dados da agência:</Typography>
                </MaterialGrid>
                <MaterialGrid item xs={7} md={6}>
                  <Field
                    component={InputLabelOutlined}
                    label={`Número${sufixoPj}*`}
                    name="payment_link_account.bankAccount.agencyNumber"
                    validate={OBRIGATORIO}
                    normalize={normalizarAgencia}
                    required
                    margin="normal"
                  />
                </MaterialGrid>
                <MaterialGrid item xs={5} md={6}>
                  <Field
                    component={InputLabelOutlined}
                    label={`Dígito${sufixoPj}`}
                    name="payment_link_account.bankAccount.agencyDv"
                    normalize={normalizarDigito}
                    margin="normal"
                  />
                </MaterialGrid>

                <MaterialGrid
                  item
                  container
                  xs={12}
                  style={{ paddingBottom: 0 }}
                >
                  <Typography variant="body2">Dados da conta:</Typography>
                </MaterialGrid>
                <MaterialGrid item xs={12} md={6}>
                  <Field
                    component={SelectLabel}
                    label="Tipo"
                    name="payment_link_account.bankAccount.accountType"
                    validate={OBRIGATORIO}
                    options={TIPOS_CONTA}
                    required
                    margin="normal"
                  />
                </MaterialGrid>
                <MaterialGrid item xs={7} md={6}>
                  <Field
                    component={InputLabelOutlined}
                    label={`Número${sufixoPj}*`}
                    name="payment_link_account.bankAccount.accountNumber"
                    validate={OBRIGATORIO}
                    normalize={normalizarConta}
                    required
                    margin="normal"
                  />
                </MaterialGrid>
                <MaterialGrid item xs={5} md={6}>
                  <Field
                    component={InputLabelOutlined}
                    label={`Dígito${sufixoPj}*`}
                    name="payment_link_account.bankAccount.accountDv"
                    normalize={normalizarDigito}
                    margin="normal"
                    required
                    validate={OBRIGATORIO}
                  />
                </MaterialGrid>
              </MaterialGrid>
            </>
          ) : null}
          <div className="row justify-content-around mb-3 mt-3">
            <SecondaryButton
              onClick={limparFormulario}
              style={{ marginRight: 16 }}
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              progress={parseInt(props.general.submitProgress)}
              router={props.router}
              type="submit"
              color="primary"
            >
              {editingAccount ? 'Atualizar Conta' : 'Salvar Conta'}
            </PrimaryButton>
          </div>
        </Form>
      )}
    </>
  );
};

PaymentLinkGatewayForm = reduxForm({
  form: FORMULARIO,
})(PaymentLinkGatewayForm);

const mapStateToProps = (state) => {
  return {
    formValues: getFormValues(FORMULARIO)(state),
    general: state.general,
    auth: state.auth,
    profile: state.profile,
    signupCompany: state.signupCompany,
  };
};
const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getAdditionalData,
      savePaymentLinkGatewayInfo,
      putPaymentLinkGatewayInfo,
      getPaymentLinkAccounts,
      getPaymentLinkAccountDetail,
      deletePaymentLinkAccount,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PaymentLinkGatewayForm);
